import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "./useWorkspace";

const WORKSPACE_ONBOARDING_TABLE = "workspace_onboarding";

export const WORKSPACE_ONBOARDING_STEPS = [
  {
    id: "connect_social",
    title: "Conectar Instagram ou Facebook",
    description: "Conecte a primeira conta social do workspace.",
    actionLabel: "Ir para Integrações",
    href: "/integracoes",
  },
  {
    id: "create_client",
    title: "Cadastrar o primeiro cliente",
    description: "Crie o primeiro cliente para começar a operação.",
    actionLabel: "Novo cliente",
    href: "/clientes?new=true",
  },
  {
    id: "create_demand",
    title: "Criar a primeira demanda",
    description: "Registre a primeira demanda do workspace.",
    actionLabel: "Nova demanda",
    href: "/demandas?new=true",
  },
  {
    id: "schedule_post",
    title: "Agendar o primeiro post",
    description: "Crie ou agende o primeiro conteúdo do calendário.",
    actionLabel: "Ir para Conteúdo",
    href: "/conteudo?new=true",
  },
] as const;

type WorkspaceOnboardingStepId = (typeof WORKSPACE_ONBOARDING_STEPS)[number]["id"];

type WorkspaceOnboardingRecord = {
  workspace_id: string;
  completed_steps: string[] | null;
  dismissed_at: string | null;
  welcome_completed_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
};

function normalizeCompletedSteps(steps?: string[] | null): WorkspaceOnboardingStepId[] {
  const availableSteps = new Set(WORKSPACE_ONBOARDING_STEPS.map((step) => step.id));
  const unique = new Set<WorkspaceOnboardingStepId>();

  (steps ?? []).forEach((step) => {
    if (availableSteps.has(step as WorkspaceOnboardingStepId)) {
      unique.add(step as WorkspaceOnboardingStepId);
    }
  });

  return WORKSPACE_ONBOARDING_STEPS
    .map((step) => step.id)
    .filter((stepId) => unique.has(stepId));
}

function sameSteps(a: WorkspaceOnboardingStepId[], b: WorkspaceOnboardingStepId[]) {
  if (a.length !== b.length) return false;
  return a.every((stepId, index) => stepId === b[index]);
}

export function useWorkspaceOnboarding() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useWorkspace();

  const query = useQuery({
    queryKey: ["workspace-onboarding", currentWorkspace?.id],
    enabled: !!currentWorkspace?.id,
    queryFn: async () => {
      if (!currentWorkspace?.id) {
        throw new Error("Workspace não encontrado");
      }

      const { data, error: onboardingError } = await supabase
        .from(WORKSPACE_ONBOARDING_TABLE)
        .select("*")
        .eq("workspace_id", currentWorkspace.id)
        .maybeSingle();

      let onboardingRecord = data;

      if (onboardingError) {
        throw onboardingError;
      }

      if (!onboardingRecord) {
        const { data: createdRecord, error: createError } = await supabase
          .from(WORKSPACE_ONBOARDING_TABLE)
          .upsert({ workspace_id: currentWorkspace.id }, { onConflict: "workspace_id" })
          .select("*")
          .single();

        if (createError) {
          throw createError;
        }

        onboardingRecord = createdRecord;
      }

      const [socialConnectionsRes, clientsRes, demandsRes, contentsRes] = await Promise.all([
        supabase
          .from("social_connections")
          .select("id", { count: "exact", head: true })
          .eq("workspace_id", currentWorkspace.id)
          .eq("is_active", true),
        supabase
          .from("clients")
          .select("id", { count: "exact", head: true })
          .eq("workspace_id", currentWorkspace.id),
        supabase
          .from("demands")
          .select("id", { count: "exact", head: true })
          .eq("workspace_id", currentWorkspace.id),
        supabase
          .from("contents")
          .select("id", { count: "exact", head: true })
          .eq("workspace_id", currentWorkspace.id)
          .or("scheduled_date.not.is.null,status.eq.agendado,status.eq.publicado,instagram_media_id.not.is.null,facebook_post_id.not.is.null"),
      ]);

      if (socialConnectionsRes.error) throw socialConnectionsRes.error;
      if (clientsRes.error) throw clientsRes.error;
      if (demandsRes.error) throw demandsRes.error;
      if (contentsRes.error) throw contentsRes.error;

      const derivedCompletedSteps = normalizeCompletedSteps([
        ...(socialConnectionsRes.count ? ["connect_social"] : []),
        ...(clientsRes.count ? ["create_client"] : []),
        ...(demandsRes.count ? ["create_demand"] : []),
        ...(contentsRes.count ? ["schedule_post"] : []),
      ]);

      const persistedCompletedSteps = normalizeCompletedSteps(
        (onboardingRecord as WorkspaceOnboardingRecord).completed_steps,
      );

      const completedSteps = normalizeCompletedSteps([
        ...persistedCompletedSteps,
        ...derivedCompletedSteps,
      ]);

      let record = onboardingRecord as WorkspaceOnboardingRecord;
      const shouldMarkCompleted = completedSteps.length === WORKSPACE_ONBOARDING_STEPS.length;
      const updates: Partial<WorkspaceOnboardingRecord> = {};

      if (!sameSteps(persistedCompletedSteps, completedSteps)) {
        updates.completed_steps = completedSteps;
      }

      if (shouldMarkCompleted && !record.completed_at) {
        updates.completed_at = new Date().toISOString();
      }

      if (Object.keys(updates).length > 0) {
        const { data: updatedRecord, error: updateError } = await supabase
          .from(WORKSPACE_ONBOARDING_TABLE)
          .update(updates)
          .eq("workspace_id", currentWorkspace.id)
          .select("*")
          .single();

        if (updateError) {
          throw updateError;
        }

        record = updatedRecord as WorkspaceOnboardingRecord;
      }

      return {
        record,
        completedSteps,
        steps: WORKSPACE_ONBOARDING_STEPS.map((step) => ({
          ...step,
          completed: completedSteps.includes(step.id),
        })),
        completedCount: completedSteps.length,
        totalSteps: WORKSPACE_ONBOARDING_STEPS.length,
        progress: (completedSteps.length / WORKSPACE_ONBOARDING_STEPS.length) * 100,
        isCompleted: completedSteps.length === WORKSPACE_ONBOARDING_STEPS.length,
        isDismissed: !!record.dismissed_at,
        shouldShowWelcome: !record.welcome_completed_at,
      };
    },
  });

  const dismiss = useMutation({
    mutationFn: async () => {
      if (!currentWorkspace?.id) {
        throw new Error("Workspace não encontrado");
      }

      const { error } = await supabase
        .from(WORKSPACE_ONBOARDING_TABLE)
        .update({ dismissed_at: new Date().toISOString() })
        .eq("workspace_id", currentWorkspace.id);

      if (error) {
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workspace-onboarding", currentWorkspace?.id] });
    },
  });

  const completeWelcome = useMutation({
    mutationFn: async () => {
      if (!currentWorkspace?.id) {
        throw new Error("Workspace não encontrado");
      }

      const { error } = await supabase
        .from(WORKSPACE_ONBOARDING_TABLE)
        .update({ welcome_completed_at: new Date().toISOString() })
        .eq("workspace_id", currentWorkspace.id);

      if (error) {
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workspace-onboarding", currentWorkspace?.id] });
    },
  });

  return {
    ...query,
    onboarding: query.data,
    dismiss,
    completeWelcome,
  };
}
